import { isFixedProduct } from '@vy/services';
import {
  INTEGRATION_PROVIDERS,
  type FixedProduct,
  type NetSuiteClass,
  type NetSuiteProductEntity,
  type UsageProduct,
} from '@vy/types';
import { isEmptyArray, isNotNullOrUndefined, isNullOrUndefined } from '@vy/utils';
import { VyStack } from '@vy/ui-design-system';
import { useEffect, useMemo } from 'react';
import { type FieldPath, Controller, useFormContext } from 'react-hook-form';
import { withSuspenseBoundary } from '../../../../../../components/utility/SuspenseBoundary';
import { useFormErrorNotificationContext } from '../../../../../../contexts/form-error-notification-context/form-error-notification-context';
import { useActivatedIntegration } from '../../../../../../hooks/use-activated-integration';
import { useNetSuiteClasses, useNetSuiteProducts } from '../../../../../../hooks/requests/integrations';
import { useChargeContext } from '../../charge-context';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';
import type { CreatePlanTab } from '../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { CREATE_PLAN_TABS } from '../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { useIntegrationEntities } from '../../../../../../hooks/use-integration-entities';
import { createNetSuiteIntegrationEntity } from '../../../../../../utils/integration-entity-creators';
import type { FormattedIntegrationProduct } from '../../../../../../utils/types/formatted-integration-product';
import { VySelect } from '@vy/ui-design-system';

const ProductNetSuiteSelectorInner: React.FC = () => {
  const isNetSuiteConnected = useActivatedIntegration(INTEGRATION_PROVIDERS.netSuite);

  const { itemIndex: productIndex, item: product } = useChargeContext<FixedProduct | UsageProduct>();
  const { registerErrorField } = useFormErrorNotificationContext<PlanInputForm, CreatePlanTab>();
  const { watch } = useFormContext<PlanInputForm>();

  const productType = isFixedProduct(product) ? 'fixedProducts' : 'usageProducts';
  const integrationEntitiesField = `${productType}.${productIndex}.integrationEntities` as const;

  const { integrationEntity, integrationEntityIndex } = useIntegrationEntities<NetSuiteProductEntity>({
    integrationEntities: watch(integrationEntitiesField),
    integrationProvider: INTEGRATION_PROVIDERS.netSuite,
  });

  const netSuiteProductField = `${integrationEntitiesField}.${integrationEntityIndex}` as const;

  const nsClass = watch(`${netSuiteProductField}.metadata.classMetadata`);

  useEffect(() => {
    registerErrorField(integrationEntitiesField, CREATE_PLAN_TABS.serviceFees);
  }, [registerErrorField, integrationEntitiesField]);

  if (!isNetSuiteConnected) {
    return null;
  }

  return (
    <VyStack direction="row" data-field-span="full" sx={{ gap: 4, '& > *': { flex: 1, minWidth: 0 } }}>
      <NetSuiteProductsSelect integrationEntitiesField={integrationEntitiesField} nsClass={nsClass} />
      <NetSuiteClassesSelect
        classFieldPath={`${netSuiteProductField}.metadata.classMetadata`}
        enabled={isNotNullOrUndefined(integrationEntity)}
      />
    </VyStack>
  );
};

type NetSuiteProductsSelectProps = {
  integrationEntitiesField: string;
  nsClass: NetSuiteClass | null | undefined;
};

const NetSuiteProductsSelect: React.FC<NetSuiteProductsSelectProps> = ({
  integrationEntitiesField,
  nsClass,
}) => {
  const { control } = useFormContext<PlanInputForm>();
  const { data: { products } = {}, isLoading } = useNetSuiteProducts(undefined, {
    enabled: true,
    suspense: false,
  });

  const options = useMemo((): FormattedIntegrationProduct[] => {
    if (isEmptyArray(products)) return [];
    return products.map(({ id, name }) => ({ id, name }));
  }, [products]);

  return (
    <Controller
      control={control}
      name={integrationEntitiesField as FieldPath<PlanInputForm>}
      render={({ field }) => {
        const currentEntities = Array.isArray(field.value) ? field.value : [];
        const currentEntity =
          currentEntities.find(
            (entity) => entity?.integrationProvider === INTEGRATION_PROVIDERS.netSuite,
          ) ?? null;
        const matchingProduct =
          isNotNullOrUndefined(currentEntity) && !isEmptyArray(options)
            ? options.find((option) => option.id === currentEntity.integrationExternalId) ?? null
            : null;

        const handleChange = (_event: React.SyntheticEvent, value: FormattedIntegrationProduct | null) => {
          const withoutNetSuite = currentEntities.filter(
            (entity) => entity?.integrationProvider !== INTEGRATION_PROVIDERS.netSuite,
          );
          const newEntity = createNetSuiteIntegrationEntity(value);

          if (isNullOrUndefined(newEntity)) {
            field.onChange(isEmptyArray(withoutNetSuite) ? null : withoutNetSuite);
            return;
          }

          const netSuiteEntity = isNotNullOrUndefined(nsClass)
            ? { ...newEntity, metadata: { ...newEntity.metadata, classMetadata: nsClass } }
            : newEntity;

          field.onChange([...withoutNetSuite, netSuiteEntity]);
        };

        return (
          <VySelect<FormattedIntegrationProduct>
            label="NetSuite Products"
            helperText="Link to your internal ERP system for tracking and reporting"
            placeholder="Select NetSuite Product"
            disabled={isLoading}
            loading={isLoading}
            options={options}
            getOptionLabel={(option) => option.name}
            getOptionKey={(option) => option.id}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={matchingProduct}
            onChange={handleChange}
            data-vy="plan-charge-drawer-netsuite-product"
          />
        );
      }}
    />
  );
};

type NetSuiteClassesSelectProps = {
  classFieldPath: string;
  enabled: boolean;
};

const NetSuiteClassesSelect: React.FC<NetSuiteClassesSelectProps> = ({ classFieldPath, enabled }) => {
  const { control } = useFormContext<PlanInputForm>();
  const classes = useNetSuiteClasses(undefined, {
    enabled,
    suspense: false,
  });

  return (
    <Controller
      key={classes.length}
      control={control}
      name={classFieldPath as FieldPath<PlanInputForm>}
      render={({ field }) => {
        const matchingClass = isNullOrUndefined(field.value)
          ? null
          : classes.find((netSuiteClass) => netSuiteClass.id === field.value?.id) ?? null;

        return (
          <VySelect<NetSuiteClass>
            label="Class"
            placeholder="Select NetSuite Class"
            disabled={!enabled}
            options={classes}
            getOptionLabel={(option) => option?.name ?? ''}
            getOptionKey={(option) => option?.id}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={matchingClass}
            onChange={(_event, value) => field.onChange(value)}
            data-vy="plan-charge-drawer-netsuite-class"
          />
        );
      }}
    />
  );
};

const ProductNetSuiteSelectorWithSuspenseBoundary = withSuspenseBoundary(ProductNetSuiteSelectorInner);

export { ProductNetSuiteSelectorWithSuspenseBoundary as ProductNetSuiteSelector };
